import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import AdminFormPage from "../../components/admin/AdminFormPage";
import { adminFormFullWidthClass, adminFormGridClass } from "../../components/admin/adminStyles";

export default function MeetGreetChargeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    pickupLocation: "",
    postCode: "",
    latitude: "",
    longitude: "",
    charge: "",
    status: true,
    remarks: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit && id) {
      axiosInstance.get(`/meet-greet-charges/${id}`).then((res) => {
        const d = res.data;
        setForm({
          pickupLocation: d.pickupLocation ?? "",
          postCode: d.postCode ?? "",
          latitude: d.latitude != null ? String(d.latitude) : "",
          longitude: d.longitude != null ? String(d.longitude) : "",
          charge: String(d.charge ?? ""),
          status: d.status ?? true,
          remarks: d.remarks ?? "",
        });
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        pickupLocation: form.pickupLocation,
        postCode: form.postCode,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        radiusMiles: 0.2,
        charge: Number(form.charge),
        status: form.status,
        remarks: form.remarks || null,
      };

      if (isEdit) {
        await axiosInstance.put(`/meet-greet-charges/${id}`, payload);
      } else {
        await axiosInstance.post("/meet-greet-charges", payload);
      }
      navigate("/meet-greet-charges");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to save";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminFormPage
      pageTitle={isEdit ? "Edit Meet & Greet Charge" : "Add Meet & Greet Charge"}
      description="Configure meet and greet pickup charges by location"
      error={error}
      onSubmit={handleSubmit}
      listRoute="/meet-greet-charges"
      saving={loading}
      saveLabel={isEdit ? "Update" : "Add"}
    >
      <div className={adminFormGridClass}>
        <InputWithLabel
          label="Pickup location"
          name="pickupLocation"
          value={form.pickupLocation}
          onChange={(e) => setForm((f) => ({ ...f, pickupLocation: e.target.value }))}
          required
        />
        <InputWithLabel
          label="Postcode"
          name="postCode"
          value={form.postCode}
          onChange={(e) => setForm((f) => ({ ...f, postCode: e.target.value }))}
          required
        />
        <InputWithLabel
          label="Charge (£)"
          name="charge"
          type="number"
          value={form.charge}
          onChange={(e) => setForm((f) => ({ ...f, charge: e.target.value }))}
          required
        />
        <InputWithLabel
          label="Latitude (optional)"
          name="latitude"
          type="number"
          value={form.latitude}
          onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
        />
        <InputWithLabel
          label="Longitude (optional)"
          name="longitude"
          type="number"
          value={form.longitude}
          onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 xl:col-span-3">
          <input
            type="checkbox"
            checked={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked }))}
          />
          Active
        </label>
        <div className={adminFormFullWidthClass}>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Remarks
          </label>
          <textarea
            name="remarks"
            value={form.remarks}
            onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
            rows={3}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          />
        </div>
      </div>
    </AdminFormPage>
  );
}
